import { useState, useEffect, useCallback, useMemo } from 'react'
import { ProjectDataResult, ServiceProvider, TransformedProjectAPIResponse } from '../types/dataManager'
import { useDataProvider, useCachedData } from '../context/DataProviderContext'
import { historyService } from '../services/historyService'

export const useProjectData = (projectId?: string): ProjectDataResult => {
  const {
    cache,
    resolveProjectProvider,
    getService
  } = useDataProvider()
  const { getCachedProject } = useCachedData()

  // State
  const [project, setProject] = useState<TransformedProjectAPIResponse | null>(null)
  const [provider, setProvider] = useState<ServiceProvider>('not-set')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const resolvedProjectId = projectId || ''

  // Main data loading function
  const loadProjectData = useCallback(async () => {
    if (!resolvedProjectId) {
      setError('No project ID provided.')
      setProject(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Check cache first
      const cachedProject = getCachedProject(resolvedProjectId)
      
      // Check if we have a cached provider for this project
      const cachedProvider = cache.current.providerMap.get(`project_${resolvedProjectId}`)
      const providerTimestamp = cache.current.lastFetched.get(`provider_project_${resolvedProjectId}`)
      const isProviderCacheValid = providerTimestamp && (Date.now() - providerTimestamp < 5 * 60 * 1000) // 5 minutes
      
      let projectProvider: ServiceProvider
      
      if (cachedProvider && isProviderCacheValid && cachedProvider !== 'not-set') {
        console.debug(`🔄 Using cached provider ${cachedProvider} for project ${resolvedProjectId}`)
        projectProvider = cachedProvider
        setProvider(projectProvider)
      } else {
        // Resolve provider
        console.debug(`🔍 Resolving provider for project ${resolvedProjectId} (cached: ${cachedProvider}, valid: ${isProviderCacheValid})`)
        projectProvider = await resolveProjectProvider(resolvedProjectId)
        console.debug(`✅ Resolved provider ${projectProvider} for project ${resolvedProjectId}`)
        
        // If provider is 'not-set', it means neither service has the project
        // In this case, we should not cache it and return early
        if (projectProvider === 'not-set') {
          console.warn(`Provider could not be determined for project ${resolvedProjectId}`)
          setProvider('not-set')
          setError('Project not found in either service')
          setIsLoading(false)
          return
        }
        
        setProvider(projectProvider)
        
        // Cache the provider resolution
        cache.current.providerMap.set(`project_${resolvedProjectId}`, projectProvider)
        cache.current.lastFetched.set(`provider_project_${resolvedProjectId}`, Date.now())
      }
      
      // If we have cached project data, use it (projectProvider is guaranteed to not be 'not-set' at this point)
      if (cachedProject) {
        setProject(cachedProject)
        setIsLoading(false)
        return
      }

      const service = getService(projectProvider)

      // Try to fetch project directly by ID first
      let projectResult: TransformedProjectAPIResponse | null = null
      try {
        projectResult = await service.getProject(resolvedProjectId)
      } catch (directError) {
        // If direct fetch fails, try by UUID (projectId might actually be a UUID)
        console.info('Direct project fetch failed, trying getProjectByUuid:', directError)
        try {
          projectResult = await service.getProjectByUuid(resolvedProjectId)
        } catch (uuidError) {
          throw new Error(`Project not found with ID: ${resolvedProjectId}`)
        }
      }

      if (projectResult) {
        setProject(projectResult)

        // Cache project
        cache.current.projects.set(resolvedProjectId, projectResult)
        cache.current.lastFetched.set(`project_${resolvedProjectId}`, Date.now())

        // Add to history
        historyService.addProject(
          projectResult.uuid,
          projectResult.info.identification,
          projectResult.info.subIdentification
        )
      } else {
        throw new Error(`Project not found: ${resolvedProjectId}`)
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load project data'
      setError(errorMessage)
      console.error('Project data loading error:', err)
      setProject(null)
    } finally {
      setIsLoading(false)
    }
  }, [resolvedProjectId, resolveProjectProvider, getService, cache, getCachedProject])

  // Refresh function
  const refresh = useCallback(async (): Promise<void> => {
    // Clear cache for this project
    cache.current.projects.delete(resolvedProjectId)
    cache.current.lastFetched.delete(`project_${resolvedProjectId}`)
    cache.current.providerMap.delete(`project_${resolvedProjectId}`)
    cache.current.lastFetched.delete(`provider_project_${resolvedProjectId}`)

    await loadProjectData()
  }, [cache, resolvedProjectId, loadProjectData])

  // Load data on mount or projectId change
  useEffect(() => {
    loadProjectData()
  }, [loadProjectData])

  const serviceWrapper = useMemo(() => {
    if (provider === 'not-set') {
      console.debug(`⏸️ Provider not set yet, returning null service wrapper`)
      return null
    }
    console.debug(`🔧 Creating service wrapper for provider: ${provider}`)
    return getService(provider)
  }, [provider, getService])

  return {
    project,
    linkedStructures: project?.linkedStructures || [],
    placements: project?.placements || [],
    projectId: resolvedProjectId,
    provider,
    isLoading,
    error,
    refresh,
    service: serviceWrapper
  }
}

// Hook for finding project data from an asset UUID
export const useProjectFromAsset = (uuid?: string) => {
  const {
    cache,
    resolveProjectFromAssetProvider,
    getService
  } = useDataProvider()
  const { getCachedProject, getCachedAsset, getCachedAssetCore } = useCachedData()

  // State
  const [project, setProject] = useState<TransformedProjectAPIResponse | null>(null)
  const [asset, setAsset] = useState<unknown>(null)
  const [assetCore, setAssetCore] = useState<unknown>(null)
  const [provider, setProvider] = useState<ServiceProvider>('not-set')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isProjectInstaller, setIsProjectInstaller] = useState(false)

  const resolvedUuid = uuid || ''

  // Main data loading function
  const loadProjectFromAsset = useCallback(async () => {
    if (!resolvedUuid) {
      setError('No UUID provided.')
      setProject(null)
      setAsset(null)
      setAssetCore(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Check if we have cached project data for this asset
      const cachedProject = getCachedProject(`asset_${resolvedUuid}`)
      const cachedAsset = getCachedAsset(resolvedUuid)
      const cachedAssetCore = getCachedAssetCore(resolvedUuid)
      
      // Check if we have a cached provider for this asset's project
      const cachedProvider = cache.current.providerMap.get(`project_from_asset_${resolvedUuid}`)
      const providerTimestamp = cache.current.lastFetched.get(`provider_project_from_asset_${resolvedUuid}`)
      const isProviderCacheValid = providerTimestamp && (Date.now() - providerTimestamp < 5 * 60 * 1000) // 5 minutes
      
      let projectProvider: ServiceProvider
      
      if (cachedProvider && isProviderCacheValid && cachedProvider !== 'not-set') {
        console.debug(`🔄 Using cached provider ${cachedProvider} for project from asset ${resolvedUuid}`)
        projectProvider = cachedProvider
        setProvider(projectProvider)
      } else {
        // Resolve provider for finding project from asset
        console.debug(`🔍 Resolving provider for project from asset ${resolvedUuid} (cached: ${cachedProvider}, valid: ${isProviderCacheValid})`)
        projectProvider = await resolveProjectFromAssetProvider(resolvedUuid)
        console.debug(`✅ Resolved provider ${projectProvider} for project from asset ${resolvedUuid}`)
        
        // If provider is 'not-set', it means neither service has the project for this asset
        // In this case, we should not cache it and return early
        if (projectProvider === 'not-set') {
          console.warn(`Provider could not be determined for project from asset ${resolvedUuid}`)
          setProvider('not-set')
          setError('Project not found in either service for this asset')
          setIsLoading(false)
          return
        }
        
        setProvider(projectProvider)
        
        // Cache the provider resolution
        cache.current.providerMap.set(`project_from_asset_${resolvedUuid}`, projectProvider)
        cache.current.lastFetched.set(`provider_project_from_asset_${resolvedUuid}`, Date.now())
      }

      // If we have all cached data, use it (projectProvider is guaranteed to not be 'not-set' at this point)
      if (cachedProject && cachedAsset && cachedAssetCore) {
        setProject(cachedProject)
        setAsset(cachedAsset)
        setAssetCore(cachedAssetCore)
        setIsLoading(false)
        return
      }

      const service = getService(projectProvider)

      // Fetch project that contains this asset
      const projectResult = await service.getProjectByUuid(resolvedUuid)
      if (projectResult) {
        setProject(projectResult)

        // Cache project with asset key
        cache.current.projects.set(`asset_${resolvedUuid}`, projectResult)
        cache.current.lastFetched.set(`project_asset_${resolvedUuid}`, Date.now())

        // Add to history
        historyService.addProject(
          projectResult.uuid,
          projectResult.info.identification,
          projectResult.info.subIdentification
        )

        // Check installer permissions
        try {
          const hasPermission = await service.checkInstallerProjectPermission(resolvedUuid)
          setIsProjectInstaller(hasPermission)
        } catch (permError) {
          console.warn('Failed to check installer permissions:', permError)
          setIsProjectInstaller(false)
        }
      }

      // Try to fetch asset data and core if not in cache
      if (!cachedAsset) {
        try {
          const assetCoreResult = await service.getAssetCore(resolvedUuid)
          setAssetCore(assetCoreResult)
          cache.current.assetCores.set(resolvedUuid, assetCoreResult)
          cache.current.lastFetched.set(`assetCore_${resolvedUuid}`, Date.now())
        } catch (coreError) {
          console.warn('Failed to fetch asset core:', coreError)
        }
      } else {
        setAsset(cachedAsset)
        setAssetCore(cachedAssetCore)
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load project from asset'
      setError(errorMessage)
      console.error('Project from asset loading error:', err)
      setProject(null)
    } finally {
      setIsLoading(false)
    }
  }, [
    resolvedUuid,
    resolveProjectFromAssetProvider,
    getService,
    cache,
    getCachedProject,
    getCachedAsset,
    getCachedAssetCore
  ])

  const serviceWrapper = useMemo(() => {
    if (provider === 'not-set') {
      console.debug(`⏸️ Provider not set yet, returning null service wrapper`)
      return null
    }
    console.debug(`🔧 Creating service wrapper for provider: ${provider}`)
    return getService(provider)
  }, [provider, getService])

  // Refresh function
  const refresh = useCallback(async (): Promise<void> => {
    // Clear cache for this asset-project relationship
    cache.current.projects.delete(`asset_${resolvedUuid}`)
    cache.current.lastFetched.delete(`project_asset_${resolvedUuid}`)
    cache.current.providerMap.delete(`project_from_asset_${resolvedUuid}`)
    cache.current.lastFetched.delete(`provider_project_from_asset_${resolvedUuid}`)
    cache.current.assetCores.delete(resolvedUuid)
    cache.current.lastFetched.delete(`assetCore_${resolvedUuid}`)

    await loadProjectFromAsset()
  }, [cache, resolvedUuid, loadProjectFromAsset])

  // Load data on mount or UUID change
  useEffect(() => {
    loadProjectFromAsset()
  }, [loadProjectFromAsset])

  return {
    project,
    asset,
    assetCore,
    uuid: resolvedUuid,
    provider,
    isLoading,
    error,
    refresh,
    isProjectInstaller,
    service: serviceWrapper
  }
}